import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import ProtectedRoute from "@/components/ProtectedRoute";

// Public
import Home from "@/pages/public/Home";
import MembersDirectory from "@/pages/public/MembersDirectory";
import ProjectsPage from "@/pages/public/ProjectsPage";
import ProjectDetailPage from "@/pages/public/ProjectDetailPage";
import MemberProfile from "@/pages/public/MemberProfile";
import EventsList from "@/pages/public/EventsList";
import EventSubmitPage from "@/pages/public/EventSubmitPage";
import AdminEventSubmissions from "@/pages/admin/AdminEventSubmissions";
import EventDetail from "@/pages/public/EventDetail";
import EventPaymentSuccess from "@/pages/public/EventPaymentSuccess";
import ExhibitorPaymentSuccess from "@/pages/public/ExhibitorPaymentSuccess";
import NewsList from "@/pages/public/NewsList";
import NewsDetail from "@/pages/public/NewsDetail";
import JoinPage from "@/pages/public/JoinPage";
import ApplyPage from "@/pages/public/ApplyPage";
import ApplyThanksPage from "@/pages/public/ApplyThanksPage";
import DigestSubscribePage from "@/pages/public/DigestSubscribePage";
import HelpPage from "@/pages/HelpPage";
import AboutPage from "@/pages/public/AboutPage";
import BillingPage from "@/pages/public/BillingPage";
import BillingSuccess from "@/pages/public/BillingSuccess";

// Auth
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

// Portal (member)
import MemberDashboard from "@/pages/portal/MemberDashboard";
import PortalProfile from "@/pages/portal/MemberProfile";
import PersonalProfile from "@/pages/portal/PersonalProfile";
import MemberNews from "@/pages/portal/MemberNews";
import MemberEvents from "@/pages/portal/MemberEvents";
import MemberDirectory from "@/pages/portal/MemberDirectory";
import MemberMeetings from "@/pages/portal/MemberMeetings";
import MemberMessages from "@/pages/portal/MemberMessages";
import MemberResources from "@/pages/portal/MemberResources";
import MemberMedia from "@/pages/portal/MemberMedia";
import MemberClub from "@/pages/portal/MemberClub";
import AdminResources from "@/pages/admin/AdminResources";
import AdminClub from "@/pages/admin/AdminClub";
import PetitionsList from "@/pages/public/PetitionsList";
import PetitionDetail from "@/pages/public/PetitionDetail";
import OpportunitiesList from "@/pages/public/OpportunitiesList";
import OpportunityDetail from "@/pages/public/OpportunityDetail";
import AdminPetitions from "@/pages/admin/AdminPetitions";
import MemberSharedServices from "@/pages/portal/MemberSharedServices";
import MemberProducts from "@/pages/portal/MemberProducts";
import MemberTeam from "@/pages/portal/MemberTeam";
import ExhibitorMatchmaking from "@/pages/portal/ExhibitorMatchmaking";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminApplicationDetail from "@/pages/admin/AdminApplicationDetail";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminNews from "@/pages/admin/AdminNews";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminEventDetail from "@/pages/admin/AdminEventDetail";
import AdminMeetings from "@/pages/admin/AdminMeetings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmailing from "@/pages/admin/AdminEmailing";
import AdminOpportunities from "@/pages/admin/AdminOpportunities";
import AdminForms from "@/pages/admin/AdminForms";
import FormView from "@/pages/public/FormView";
import NewsletterConfirm from "@/pages/public/NewsletterConfirm";
import AdminDigest from "@/pages/admin/AdminDigest";
import AdminActivity from "@/pages/admin/AdminActivity";
import PublicMeetingRequest from "@/pages/public/PublicMeetingRequest";
import ActivateAccount from "@/pages/public/ActivateAccount";
import MyCompany from "@/pages/portal/MyCompany";
import Notifications from "@/pages/portal/Notifications";
import MyScore from "@/pages/portal/MyScore";
import AdminScoring from "@/pages/admin/AdminScoring";
import AdminRenewals from "@/pages/admin/AdminRenewals";
import RenewalForm from "@/pages/public/RenewalForm";
import Leaderboard from "@/pages/public/Leaderboard";
import Matchmaking from "@/pages/portal/Matchmaking";
import AdminFormations from "@/pages/admin/AdminFormations";
import AdminFormationDetail from "@/pages/admin/AdminFormationDetail";
import AdminSharedServices from "@/pages/admin/AdminSharedServices";
import FormationsList from "@/pages/public/FormationsList";
import FormationDetail from "@/pages/public/FormationDetail";
import AdminScan from "@/pages/admin/AdminScan";
import AdminMemberVisuals from "@/pages/admin/AdminMemberVisuals";
import AdminEventVisuals from "@/pages/admin/AdminEventVisuals";
import AdminAIFeedback from "@/pages/admin/AdminAIFeedback";
import AIAssistantWidget from "@/components/AIAssistantWidget";
import ContactAudreyBubble from "@/components/ContactAudreyBubble";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";
import LegacyEventRedirect from "@/pages/portal/LegacyEventRedirect";
import EventSpace from "@/pages/portal/event-space/EventSpace";
import AdminCompanyDetail from "@/pages/admin/AdminCompanyDetail";
import AdminProspects from "@/pages/admin/AdminProspects";
import ContactPage from "@/pages/public/ContactPage";

// Press
import PressDashboard from "@/pages/press/PressDashboard";
import PressReleases from "@/pages/press/PressReleases";
import PressEvents from "@/pages/press/PressEvents";
import PressMembers from "@/pages/press/PressMembers";
import PressResources from "@/pages/press/PressResources";
import NotFound from "@/pages/public/NotFound";

import "@/App.css";

function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/rdv" element={<PublicMeetingRequest />} />
            <Route path="/meeting-request" element={<PublicMeetingRequest />} />
            <Route path="/activate" element={<ActivateAccount />} />
            <Route path="/portal/my-company" element={<ProtectedRoute roles={["member", "admin"]}><MyCompany /></ProtectedRoute>} />
            <Route path="/portal/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/portal/score" element={<ProtectedRoute roles={["member", "admin"]}><MyScore /></ProtectedRoute>} />
            <Route path="/admin/scoring" element={<ProtectedRoute roles={["admin"]}><AdminScoring /></ProtectedRoute>} />
            <Route path="/admin/renewals" element={<ProtectedRoute roles={["admin"]}><AdminRenewals /></ProtectedRoute>} />
            <Route path="/renewal" element={<RenewalForm />} />
            <Route path="/renewal/success" element={<RenewalForm />} />
            <Route path="/community/top" element={<Leaderboard />} />
            <Route path="/portal/matchmaking" element={<ProtectedRoute roles={["member", "admin"]}><Matchmaking /></ProtectedRoute>} />
            <Route path="/admin/formations" element={<ProtectedRoute roles={["admin"]}><AdminFormations /></ProtectedRoute>} />
            <Route path="/formations" element={<FormationsList />} />
            <Route path="/formations/:slug" element={<FormationDetail />} />
            <Route path="/admin/scan" element={<ProtectedRoute roles={["admin"]}><AdminScan /></ProtectedRoute>} />
            <Route path="/admin/members/:slug/visuals" element={<ProtectedRoute roles={["admin"]}><AdminMemberVisuals /></ProtectedRoute>} />
            <Route path="/admin/events/:id/visuals" element={<ProtectedRoute roles={["admin"]}><AdminEventVisuals /></ProtectedRoute>} />
            <Route path="/members" element={<MembersDirectory />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:slug" element={<ProjectDetailPage />} />
            <Route path="/members/:slug" element={<MemberProfile />} />
            <Route path="/events" element={<EventsList />} />
            <Route path="/evenements" element={<Navigate to="/events" replace />} />
            <Route path="/events/publier" element={<EventSubmitPage />} />
            <Route path="/evenements/publier" element={<EventSubmitPage />} />
            <Route path="/events/:slug" element={<EventDetail />} />
            <Route path="/events/:slug/success" element={<EventPaymentSuccess />} />
            <Route path="/events/:slug/exhibitor-success" element={<ExhibitorPaymentSuccess />} />
            <Route path="/news" element={<NewsList />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/apply/thanks" element={<ApplyThanksPage />} />
            <Route path="/digest" element={<DigestSubscribePage />} />
            <Route path="/digest/subscribe" element={<DigestSubscribePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/billing/:appId" element={<BillingPage />} />
            <Route path="/billing/:appId/success" element={<BillingSuccess />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Member Portal */}
            <Route path="/portal" element={<ProtectedRoute roles={["member","applicant","event_user","admin"]}><MemberDashboard /></ProtectedRoute>} />
            <Route path="/portal/event/:slug/:section?" element={<ProtectedRoute roles={["member","applicant","event_user","admin","press","journalist"]}><EventSpace /></ProtectedRoute>} />
            <Route path="/portal/events/:slug" element={<ProtectedRoute roles={["member","applicant","event_user","admin","press","journalist"]}><LegacyEventRedirect /></ProtectedRoute>} />
            <Route path="/portal/profile" element={<ProtectedRoute><PortalProfile /></ProtectedRoute>} />
            <Route path="/portal/me" element={<ProtectedRoute><PersonalProfile /></ProtectedRoute>} />
            <Route path="/portal/products" element={<ProtectedRoute><MemberProducts /></ProtectedRoute>} />
            <Route path="/portal/team" element={<ProtectedRoute><MemberTeam /></ProtectedRoute>} />
            <Route path="/portal/news" element={<ProtectedRoute><MemberNews /></ProtectedRoute>} />
            <Route path="/portal/events" element={<ProtectedRoute><MemberEvents /></ProtectedRoute>} />
            <Route path="/portal/directory" element={<ProtectedRoute><MemberDirectory /></ProtectedRoute>} />
            <Route path="/portal/meetings" element={<ProtectedRoute><MemberMeetings /></ProtectedRoute>} />
            <Route path="/portal/messages" element={<ProtectedRoute><MemberMessages /></ProtectedRoute>} />
            <Route path="/portal/resources" element={<ProtectedRoute><MemberResources /></ProtectedRoute>} />
            <Route path="/portal/media" element={<ProtectedRoute roles={["member","admin"]}><MemberMedia /></ProtectedRoute>} />
            <Route path="/portal/club" element={<ProtectedRoute><MemberClub /></ProtectedRoute>} />
            <Route path="/admin/resources" element={<ProtectedRoute roles={["admin"]}><AdminResources /></ProtectedRoute>} />
            <Route path="/admin/club" element={<ProtectedRoute roles={["admin"]}><AdminClub /></ProtectedRoute>} />
            <Route path="/soutenez-nous" element={<PetitionsList />} />
            <Route path="/soutenez-nous/:slug" element={<PetitionDetail />} />
            <Route path="/opportunites" element={<OpportunitiesList />} />
            <Route path="/opportunites/:slug" element={<OpportunityDetail />} />
            <Route path="/admin/petitions" element={<ProtectedRoute roles={["admin"]}><AdminPetitions /></ProtectedRoute>} />
            <Route path="/admin/event-submissions" element={<ProtectedRoute roles={["admin"]}><AdminEventSubmissions /></ProtectedRoute>} />
            <Route path="/admin/forms" element={<ProtectedRoute roles={["admin"]}><AdminForms /></ProtectedRoute>} />
            <Route path="/formulaires/:slug" element={<FormView />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/portal/shared-services" element={<ProtectedRoute roles={["member","admin"]}><MemberSharedServices /></ProtectedRoute>} />
            <Route path="/admin/shared-services" element={<ProtectedRoute roles={["admin"]}><AdminSharedServices /></ProtectedRoute>} />
            <Route path="/admin/formations/:id" element={<ProtectedRoute roles={["admin"]}><AdminFormationDetail /></ProtectedRoute>} />
            <Route path="/portal/matchmaking/:exhibitorId" element={<ProtectedRoute><ExhibitorMatchmaking /></ProtectedRoute>} />
            <Route path="/portal/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute roles={["admin"]}><AdminApplications /></ProtectedRoute>} />
            <Route path="/admin/applications/:id" element={<ProtectedRoute roles={["admin"]}><AdminApplicationDetail /></ProtectedRoute>} />
            <Route path="/admin/pipeline" element={<Navigate to="/admin/prospects" replace />} />
            <Route path="/admin/members" element={<ProtectedRoute roles={["admin"]}><AdminMembers /></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute roles={["admin"]}><AdminContacts /></ProtectedRoute>} />
            <Route path="/admin/companies/:id" element={<ProtectedRoute roles={["admin"]}><AdminCompanyDetail /></ProtectedRoute>} />
            <Route path="/admin/prospects" element={<ProtectedRoute roles={["admin"]}><AdminProspects /></ProtectedRoute>} />
            <Route path="/admin/news" element={<ProtectedRoute roles={["admin"]}><AdminNews /></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute roles={["admin"]}><AdminEvents /></ProtectedRoute>} />
            <Route path="/admin/events/:id" element={<ProtectedRoute roles={["admin"]}><AdminEventDetail /></ProtectedRoute>} />
            <Route path="/admin/meetings" element={<ProtectedRoute roles={["admin"]}><AdminMeetings /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute roles={["admin"]}><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/emailing" element={<ProtectedRoute roles={["admin"]}><AdminEmailing /></ProtectedRoute>} />
            <Route path="/admin/emails" element={<Navigate to="/admin/emailing?tab=config" replace />} />
            <Route path="/admin/emailings" element={<Navigate to="/admin/emailing?tab=templates" replace />} />
            <Route path="/admin/opportunities" element={<ProtectedRoute roles={["admin"]}><AdminOpportunities /></ProtectedRoute>} />
            <Route path="/admin/mailshots" element={<Navigate to="/admin/emailing?tab=campaigns" replace />} />
            <Route path="/admin/digest" element={<ProtectedRoute roles={["admin"]}><AdminDigest /></ProtectedRoute>} />
            <Route path="/admin/direction" element={<Navigate to="/admin/analytics" replace />} />
            <Route path="/admin/activity" element={<ProtectedRoute roles={["admin"]}><AdminActivity /></ProtectedRoute>} />
            <Route path="/admin/ai-feedback" element={<ProtectedRoute roles={["admin"]}><AdminAIFeedback /></ProtectedRoute>} />
            <Route path="/admin/help" element={<ProtectedRoute roles={["admin"]}><HelpPage /></ProtectedRoute>} />

            {/* Press */}
            <Route path="/press" element={<ProtectedRoute roles={["journalist","admin"]}><PressDashboard /></ProtectedRoute>} />
            <Route path="/press/releases" element={<ProtectedRoute roles={["journalist","admin"]}><PressReleases /></ProtectedRoute>} />
            <Route path="/press/events" element={<ProtectedRoute roles={["journalist","admin"]}><PressEvents /></ProtectedRoute>} />
            <Route path="/press/members" element={<ProtectedRoute roles={["journalist","admin"]}><PressMembers /></ProtectedRoute>} />
            <Route path="/press/resources" element={<ProtectedRoute roles={["journalist","admin"]}><PressResources /></ProtectedRoute>} />
            <Route path="/press/help" element={<ProtectedRoute roles={["journalist","admin"]}><HelpPage /></ProtectedRoute>} />
            {/* 404 fallback — must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <AIAssistantWidget />
          <ContactAudreyBubble />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  </HelmetProvider>
  );
}

export default App;
